import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { interval, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs/operators';
import { StocktakeLocationLine } from 'src/interfaces/stocktake-location';
import { StocktakeService } from 'src/core/services/stocktake.service';

const LIVE_REFRESH_MS = 30_000;

/** Maps Material column ids to backend `order_by` values. */
const ORDER_BY_FOR_COLUMN: Record<string, string> = {
  medicineName: 'medicine_name',
  itemLabel: 'item_label',
  strengthLabel: 'strength_label',
  packagingLabel: 'packaging_label',
  onHand: 'on_hand',
  allocated: 'allocated',
  onHold: 'on_hold',
  total: 'total',
};

@Component({
    selector: 'app-stocktake-location-live',
    templateUrl: './stocktake-location-live.component.html',
    styleUrls: ['./stocktake-location-live.component.scss'],
    standalone: false
})
export class StocktakeLocationLiveComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'medicineName',
    'itemLabel',
    'strengthLabel',
    'packagingLabel',
    'packSize',
    'onHand',
    'allocated',
    'onHold',
    'total',
    'lowStock',
  ];

  /** Header tooltips for quantity columns (business definitions for this view). */
  readonly qtyHeaderTooltips = {
    onHand: 'On hand = free stock available now (can still allocate)',
    allocated: 'Allocated = units on open orders (reserved + packed, not shipped)',
    onHold: 'On hold = written off / not countable',
    total: 'Total = live buckets including allocated and on hold (not on-hand alone)',
  };

  dataSource = new MatTableDataSource<StocktakeLocationLine>([]);
  loading = false;
  lastUpdated: Date | null = null;
  loadError = false;

  /** 1-based page (matches backend). */
  page = 1;
  limit = 20;
  totalCount = 0;

  orderBy = 'medicine_name';
  orderDirection: 'asc' | 'desc' = 'asc';

  filtersForm = this._fb.group({
    search: [''],
    autoRefresh: [true],
  });

  private readonly _destroy$ = new Subject<void>();
  private _loadGen = 0;

  constructor(
    private _stocktake: StocktakeService,
    private _fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.filtersForm
      .get('search')
      ?.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this._destroy$)
      )
      .subscribe(() => this.load({ resetPage: true }));

    interval(LIVE_REFRESH_MS)
      .pipe(
        takeUntil(this._destroy$),
        filter(() => !!this.filtersForm.get('autoRefresh')?.value)
      )
      .subscribe(() => this.load());

    this.filtersForm
      .get('autoRefresh')
      ?.valueChanges.pipe(takeUntil(this._destroy$))
      .subscribe((on) => {
        if (on) {
          this.load();
        }
      });

    this.load({ resetPage: true });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * @param resetPage Go to page 1 (after search / filters / sort change).
   */
  async load(opts?: { resetPage?: boolean }): Promise<void> {
    const gen = ++this._loadGen;
    if (opts?.resetPage) {
      this.page = 1;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
    }

    this.loading = true;
    this.loadError = false;

    const searchRaw = (this.filtersForm.get('search')?.value ?? '').trim();

    const result = await this._stocktake.getLocationLiveView({
      page: this.page,
      limit: this.limit,
      search: searchRaw,
      orderBy: this.orderBy,
      orderDirection: this.orderDirection,
      includeOnHold: true,
      includeNotYetIssued: true,
      /** With search: ask API for sibling pack lines. Empty search: omit param (smaller grid). */
      expandSiblingPackLines: !!searchRaw ? true : undefined,
    });

    if (gen !== this._loadGen) {
      return;
    }

    this.loading = false;

    if (result === null) {
      this.loadError = true;
      this.dataSource.data = [];
      this.totalCount = 0;
      return;
    }

    this.lastUpdated = new Date();
    this.dataSource.data = result.items;
    this.totalCount = result.total;
  }

  onPageChange(ev: PageEvent): void {
    this.page = ev.pageIndex + 1;
    this.limit = ev.pageSize;
    this.load();
  }

  onSortChange(sort: Sort): void {
    if (!sort.direction) {
      this.orderBy = 'medicine_name';
      this.orderDirection = 'asc';
    } else {
      this.orderBy = ORDER_BY_FOR_COLUMN[sort.active] ?? 'medicine_name';
      this.orderDirection = sort.direction === 'desc' ? 'desc' : 'asc';
    }
    this.load({ resetPage: true });
  }

  /** Pack size column: prefer API `pack_size`, else nested contained quantity string. */
  packSizeDisplay(row: StocktakeLocationLine): string {
    if (row.packSize !== null && row.packSize !== undefined) {
      return String(row.packSize);
    }
    return row.packSizeLabel || '—';
  }

  trackByRow(_index: number, row: StocktakeLocationLine): string {
    return row.rowKey;
  }
}
